import facturaVentaService, {
  FacturaVenta,
  FacturaVentaDetalle,
} from '../../../services/facturaVentaService';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import {
  cargarProductoParaDocumento,
  esTipoDiscontinuado,
  esTipoInactivo,
  evaluarUsoProductoDocumento,
} from '../../../utils/bloqueaArtDiscontinuados';
import productoService from '../../../services/productoService';
import {
  obtenerPrecioBaseProducto,
  resolverPrecioDesdeLista,
} from '../../../utils/precioListaPedido';
import {
  precioEnUmDocumento,
  umBaseProducto,
} from '../../../utils/umVentaDocumento';
import { POSLinea } from './types';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number =>
  Math.round(l.cantidad * l.precioUnitario * (1 - l.descuento / 100) * 100) / 100;

const normalizarDescuentoPorcentaje = (detalle: FacturaVentaDetalle): number => {
  let porcDesc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
  if (porcDesc > 0 && porcDesc <= 1) porcDesc *= 100;
  if (!Number.isFinite(porcDesc)) return 0;
  return Math.max(0, Math.min(100, porcDesc));
};

const esFacturaAnulada = (factura: FacturaVenta): boolean =>
  String(factura.asentado ?? '').trim().toUpperCase() === 'A';

async function resolverPrecioActual(
  producto: unknown,
  nroLista?: string,
  umDocumento?: string,
): Promise<number> {
  let precio = obtenerPrecioBaseProducto(producto);
  let umPrecioLista = umBaseProducto(producto);
  const codigo = String((producto as { codigoArticulo?: string })?.codigoArticulo ?? '').trim();
  if (nroLista && codigo) {
    try {
      const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(nroLista, codigo);
      const resultado = resolverPrecioDesdeLista(producto, reg);
      if (resultado.tipo !== 'sinPrecio') {
        precio = resultado.precioLista;
        umPrecioLista = (reg.um || '').trim() || umPrecioLista;
      }
    } catch {
      /* sin precio en lista: usa precio base del producto */
    }
  }
  const umDoc = String(umDocumento ?? '').trim() || umBaseProducto(producto);
  const umBase = umBaseProducto(producto);
  if (codigo && umDoc && umPrecioLista) {
    try {
      const detalle = await productoService.getEquivalenciasUmDetalle(codigo);
      precio = precioEnUmDocumento(
        precio,
        umDoc,
        umBase,
        detalle.equivalencias ?? [],
        umPrecioLista,
      );
    } catch {
      /* sin equivalencias: precio sin convertir */
    }
  }
  return Number.isFinite(precio) ? precio : 0;
}

export type LineaOmitidaDiscontinuada = {
  codigoArticulo: string;
  descripcion: string;
};

export type CopiarDesdeFacturaOk = {
  ok: true;
  lineas: POSLinea[];
  /** Total de líneas no copiadas (DIS + otros). */
  omitidas: number;
  /** Artículos DIS omitidos (modo 1, o modo 2 si el usuario canceló el confirm). */
  omitidasDiscontinuadas: LineaOmitidaDiscontinuada[];
  /** Omitidas por inactivo, sin precio, sin código, etc. */
  omitidasOtras: number;
  nroFormulario: number;
  productosIva: Record<string, unknown>;
};

export type CopiarDesdeFacturaFail = {
  ok: false;
  reason: 'sin_cliente' | 'sin_historial' | 'sin_factura' | 'sin_productos';
  message: string;
  omitidasDiscontinuadas?: LineaOmitidaDiscontinuada[];
};

export type CopiarDesdeFacturaResult = CopiarDesdeFacturaOk | CopiarDesdeFacturaFail;

export type ConstruirLineasDesdeFacturaParams = {
  nroFormulario: number;
  nroLista?: string;
  /** Si ya se tiene el detalle (p. ej. del listado), evita un GET extra. */
  factura?: FacturaVenta;
};

/**
 * Lista las últimas facturas de venta del cliente (no anuladas), más recientes primero.
 */
export async function listarUltimasFacturasCliente(
  nroCliente: number,
  take = 10,
): Promise<FacturaVenta[]> {
  const nro = Number(nroCliente) || 0;
  if (nro <= 0) return [];

  const page = await facturaVentaService.getFacturasVenta({
    nroCliente: nro,
    pageNumber: 1,
    pageSize: Math.min(20, Math.max(take * 2, take)),
  });

  return (page.items || [])
    .filter((f) => !esFacturaAnulada(f))
    .slice(0, take);
}

/**
 * Arma líneas POS desde una factura: mismos artículos, cantidades y descuentos,
 * con precios vigentes de lista (no el precio histórico de la factura).
 * DIS: modo 0 incluye; modo 1 omite; modo 2 confirma con el mismo mensaje del buscador.
 */
export async function construirLineasDesdeFactura(
  params: ConstruirLineasDesdeFacturaParams,
): Promise<CopiarDesdeFacturaResult> {
  const nroFormulario = Number(params.nroFormulario) || 0;
  if (nroFormulario <= 0) {
    return {
      ok: false,
      reason: 'sin_factura',
      message: 'No se indicó una factura válida.',
    };
  }

  let factura = params.factura;
  const detallesPrevios = factura?.detalles;
  if (!factura || !Array.isArray(detallesPrevios) || detallesPrevios.length === 0) {
    try {
      factura = await facturaVentaService.getFacturaVenta(nroFormulario);
    } catch {
      return {
        ok: false,
        reason: 'sin_factura',
        message: 'No se pudo obtener el detalle de la factura seleccionada.',
      };
    }
  }

  const nroLista = String(params.nroLista || '').trim() || undefined;
  const lineas: POSLinea[] = [];
  const productosIva: Record<string, unknown> = {};
  const omitidasDiscontinuadas: LineaOmitidaDiscontinuada[] = [];
  let omitidasOtras = 0;

  for (const detalle of factura.detalles || []) {
    const codigoArticulo = String(detalle.codigoArticulo || '').trim();
    if (!codigoArticulo) {
      omitidasOtras += 1;
      continue;
    }

    const cantidad = Math.max(0, Number(detalle.cantidad || 0));
    if (cantidad <= 0) {
      omitidasOtras += 1;
      continue;
    }

    let producto: Awaited<ReturnType<typeof cargarProductoParaDocumento>>;
    try {
      producto = await cargarProductoParaDocumento(codigoArticulo, 'VENTAS');
    } catch (e) {
      const msg = String(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (e as Error)?.message
        ?? '',
      );
      const descFallback =
        String(detalle.descripcionArticulo || '').trim() || codigoArticulo;
      if (/discontinuad/i.test(msg)) {
        omitidasDiscontinuadas.push({
          codigoArticulo,
          descripcion: descFallback,
        });
      } else {
        omitidasOtras += 1;
      }
      continue;
    }

    if (esTipoInactivo(producto.tipoArticulo)) {
      omitidasOtras += 1;
      continue;
    }

    const descProducto =
      String(producto.descripcion || '').trim()
      || String(detalle.descripcionArticulo || '').trim()
      || codigoArticulo;

    if (esTipoDiscontinuado(producto.tipoArticulo)) {
      const decision = await evaluarUsoProductoDocumento(
        { ...producto, descripcion: descProducto },
        'VENTAS',
      );
      if (!decision.permitido) {
        omitidasDiscontinuadas.push({
          codigoArticulo,
          descripcion: descProducto,
        });
        continue;
      }
    }

    const um = String(
      producto.um
      ?? producto.unidadMedida
      ?? detalle.um
      ?? '',
    ).trim();
    const precioUnitario = await resolverPrecioActual(producto, nroLista, um);
    if (!Number.isFinite(precioUnitario) || precioUnitario <= 0) {
      omitidasOtras += 1;
      continue;
    }

    const descuento = normalizarDescuentoPorcentaje(detalle);

    const linea: POSLinea = {
      id: genId(),
      codigoArticulo,
      descripcionArticulo: descProducto,
      cantidad,
      precioUnitario,
      descuento,
      subtotal: 0,
      um,
      notas: detalle.notas,
    };
    linea.subtotal = calcularSubtotal(linea);
    lineas.push(linea);
    productosIva[codigoArticulo] = producto;
  }

  const omitidas = omitidasDiscontinuadas.length + omitidasOtras;

  if (lineas.length === 0) {
    const soloDis =
      omitidasDiscontinuadas.length > 0 && omitidasOtras === 0;
    return {
      ok: false,
      reason: 'sin_productos',
      message: soloDis
        ? 'Todos los artículos de la factura están discontinuados y no se agregaron.'
        : 'No se pudieron mapear productos de la factura al catálogo actual.',
      omitidasDiscontinuadas,
    };
  }

  return {
    ok: true,
    lineas,
    omitidas,
    omitidasDiscontinuadas,
    omitidasOtras,
    nroFormulario: Number(factura.nroFormulario) || nroFormulario,
    productosIva,
  };
}

/** Texto corto para snackbar con códigos omitidos por DIS. */
export function mensajeOmitidasDiscontinuadas(
  omitidas: LineaOmitidaDiscontinuada[],
  maxCodigos = 5,
): string {
  if (omitidas.length === 0) return '';
  const codigos = omitidas.slice(0, maxCodigos).map((o) => o.codigoArticulo);
  const extra = omitidas.length > maxCodigos ? ` y ${omitidas.length - maxCodigos} más` : '';
  const n = omitidas.length;
  return `${n} artículo${n > 1 ? 's' : ''} discontinuado${n > 1 ? 's' : ''} no se agregaron: ${codigos.join(', ')}${extra}.`;
}
